import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import SteckLogo from "../components/SteckLogo";

const PATHS = [
  {
    id: "sales",
    label: "Aircraft Sales.",
    body: "Explore available aircraft and ownership opportunities.",
  },
  {
    id: "management",
    label: "Aircraft Management.",
    body:
      "Coordination of flight operations, maintenance, records, vendors, and cost control—under a written agreement.",
  },
];

const Welcome = ({ onDismiss }) => {
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const [pointer, setPointer] = useState({ x: 0.5, y: 0.45 });
  const [hoverIdx, setHoverIdx] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => setMounted(true), 30);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, []);

  const onMove = (e) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPointer({
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
    });
  };

  const choose = (id) => {
    try { localStorage.setItem("steck_visitor_path", id); } catch { /* ignore */ }
    try { localStorage.setItem("steck_visitor_acknowledged", "1"); } catch { /* ignore */ }
    try { sessionStorage.setItem("steck_from_vestibule", "1"); } catch { /* ignore */ }
    // Signal the destination page (Aircraft / Management) to show the travel
    // assessment on arrival. Separate from the navbar's own flag above.
    try { sessionStorage.setItem("steck_assessment_pending", "1"); } catch { /* ignore */ }
    if (onDismiss) onDismiss();
    if (id === "sales") {
      navigate("/aircraft/beechjet-400a");
    } else {
      navigate("/about");
    }
  };

  const browseLibrary = () => {
    try {
      localStorage.setItem("steck_visitor_path", "research");
      localStorage.setItem("steck_visitor_acknowledged", "1");
    } catch { /* ignore */ }
    if (onDismiss) onDismiss();
    navigate("/library");
  };

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      ref={stageRef}
      onMouseMove={onMove}
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{ background: "#03101C", color: "var(--ivory)" }}
      data-testid="welcome-modal"
    >
      {/* Soft layered gradient */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(140% 80% at 50% 0%, rgba(22,58,83,0.45) 0%, rgba(3,16,28,0) 55%), radial-gradient(120% 90% at 50% 110%, rgba(34,81,255,0.10) 0%, rgba(3,16,28,0) 60%)",
        }}
      />

      {/* Cursor-reactive glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
        style={{
          opacity: mounted ? 1 : 0,
          background: `radial-gradient(520px 520px at ${pointer.x * 100}% ${pointer.y * 100}%, rgba(103,145,255,0.10) 0%, rgba(103,145,255,0.03) 35%, rgba(3,16,28,0) 70%)`,
        }}
      />

      {/* Vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 50%, rgba(3,16,28,0) 0%, rgba(3,16,28,0.55) 70%, rgba(3,16,28,0.92) 100%)",
        }}
      />

      {/* Top chrome */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-8 md:px-16 py-7 md:py-9">
        <span />
        <div
          className="hidden md:block"
          style={{ color: "var(--slate)", fontSize: "12px", letterSpacing: "0.04em" }}
        >
          {today}
        </div>
      </div>

      {/* Bottom chrome */}
      <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between px-8 md:px-16 py-7 md:py-9">
        <span />
      </div>

      {/* Center stage — column flex + auto block margins on the inner column so
          it centers vertically WHEN it fits, but when the content is taller than
          the viewport the margins collapse and it flows from the top (scrollable)
          instead of clipping the logo off the top edge. Safe-area padding keeps
          the logo clear of the iOS notch / status bar. */}
      <div className="relative z-10 h-full w-full flex flex-col items-center px-6 md:px-16 overflow-auto">
        <div
          className="w-full max-w-[1080px]"
          style={{
            margin: "auto 0",
            paddingTop:
              "max(clamp(72px, 9vh, 128px), calc(env(safe-area-inset-top, 0px) + 40px))",
            paddingBottom:
              "max(clamp(56px, 7vh, 96px), calc(env(safe-area-inset-bottom, 0px) + 32px))",
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(14px)",
            transition: "opacity 900ms ease, transform 900ms cubic-bezier(.2,.7,.2,1)",
          }}
        >
          {/* Logo—prominent, with wipe-reveal */}
          <div
            className="mb-10 md:mb-12 vestibule-logo-reveal"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(8px)",
              transition:
                "opacity 1100ms ease 80ms, transform 1100ms cubic-bezier(.2,.7,.2,1) 80ms",
            }}
            data-testid="welcome-brand-mark"
          >
            <SteckLogo size={152} />
            <style>{`
              .vestibule-logo-reveal > img {
                -webkit-clip-path: inset(0 100% 0 0);
                        clip-path: inset(0 100% 0 0);
                animation: vestibule-logo-draw 1400ms cubic-bezier(.66,.04,.18,1) 320ms forwards;
              }
              @keyframes vestibule-logo-draw {
                0%   { -webkit-clip-path: inset(0 100% 0 0); clip-path: inset(0 100% 0 0); }
                100% { -webkit-clip-path: inset(0 0 0 0);     clip-path: inset(0 0 0 0); }
              }
              @media (prefers-reduced-motion: reduce) {
                .vestibule-logo-reveal > img {
                  animation: none;
                  -webkit-clip-path: inset(0 0 0 0);
                          clip-path: inset(0 0 0 0);
                }
              }
            `}</style>
          </div>

          <h1
            className="font-display-lg max-w-3xl"
            style={{
              fontSize: "clamp(36px, 4.8vw, 64px)",
              color: "var(--white)",
              lineHeight: 1.04,
            }}
          >
            Please indicate the nature of your visit.
          </h1>


          {/* Path cards */}
          <style>{`
            /* Mobile: flush both cards to the left. The asymmetric inset padding
               only exists to frame the desktop center divider. */
            @media (max-width: 767px) {
              .vestibule-path { padding-left: 0 !important; padding-right: 0 !important; }
            }
          `}</style>
          <div className="mt-12 md:mt-16 relative grid grid-cols-1 md:grid-cols-2">
            <div
              aria-hidden
              className="hidden md:block absolute top-2 bottom-2 left-1/2 -translate-x-1/2 w-px"
              style={{ background: "rgba(255,255,255,0.12)" }}
            />
            {PATHS.map((p, idx) => {
              const active = hoverIdx === idx;
              const dimmed = hoverIdx !== null && hoverIdx !== idx;
              const isLeft = idx === 0;
              return (
                <button
                  key={p.id}
                  onClick={() => choose(p.id)}
                  onMouseEnter={() => setHoverIdx(idx)}
                  onMouseLeave={() => setHoverIdx(null)}
                  className="text-left group relative vestibule-path"
                  style={{
                    paddingLeft: isLeft ? 0 : "clamp(28px, 4vw, 56px)",
                    paddingRight: isLeft ? "clamp(28px, 4vw, 56px)" : 0,
                    paddingTop: "28px",
                    paddingBottom: "28px",
                    opacity: dimmed ? 0.42 : 1,
                    transition: "opacity 500ms ease",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                  data-testid={`welcome-path-${p.id}`}
                >
                  {/* Framing fragments are for the 2-column desktop layout only;
                      hidden on mobile so the stacked cards read cleanly. */}
                  <div
                    aria-hidden
                    className="absolute top-0 h-px hidden md:block"
                    style={{
                      background: active ? "var(--blue-soft)" : "rgba(255,255,255,0.12)",
                      width: active ? "100%" : "32%",
                      [isLeft ? "right" : "left"]: 0,
                      transition: "width 700ms cubic-bezier(.2,.7,.2,1), background 400ms ease",
                    }}
                  />
                  <div
                    className="font-display"
                    style={{
                      fontSize: "clamp(22px, 2.1vw, 30px)",
                      color: active ? "var(--white)" : "var(--ivory)",
                      transform: active
                        ? `translateX(${isLeft ? "-4px" : "4px"})`
                        : "translateX(0)",
                      transition:
                        "color 400ms ease, transform 600ms cubic-bezier(.2,.7,.2,1)",
                      lineHeight: 1.18,
                    }}
                  >
                    {p.label}
                  </div>
                  <div
                    style={{
                      color: "var(--slate)",
                      fontSize: "14px",
                      lineHeight: 1.7,
                      marginTop: "14px",
                      opacity: active ? 1 : 0.78,
                      transform: active
                        ? `translateX(${isLeft ? "-4px" : "4px"})`
                        : "translateX(0)",
                      transition:
                        "opacity 400ms ease, transform 600ms cubic-bezier(.2,.7,.2,1)",
                    }}
                  >
                    {p.body}
                  </div>
                  <div
                    aria-hidden
                    className="mt-8 h-px hidden md:block"
                    style={{
                      background: active ? "var(--blue-soft)" : "rgba(255,255,255,0.12)",
                      width: active ? "100%" : "32%",
                      marginLeft: isLeft ? 0 : "auto",
                      transition: "width 700ms cubic-bezier(.2,.7,.2,1), background 400ms ease",
                    }}
                  />
                  {idx === 0 && (
                    <div
                      aria-hidden
                      className="md:hidden mt-10 h-px w-full"
                      style={{ background: "rgba(255,255,255,0.10)" }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Tertiary—researching only */}
          <div style={{
            marginTop: "clamp(44px,5.5vh,72px)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: "clamp(28px,3.5vh,44px)",
            display: "flex",
            justifyContent: "center",
          }}>
            <button
              onClick={browseLibrary}
              onMouseEnter={() => setHoverIdx(2)}
              onMouseLeave={() => setHoverIdx(null)}
              className="group inline-flex items-baseline gap-3"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
              data-testid="welcome-path-research"
            >
              <span
                style={{
                  color: hoverIdx === 2 ? "rgba(255,255,255,0.70)" : "rgba(255,255,255,0.28)",
                  fontSize: "13px",
                  letterSpacing: "0.02em",
                  transition: "color 350ms ease",
                }}
              >
                I am only researching for now
              </span>
              <span
                className="inline-block"
                style={{
                  height: "1px",
                  background: hoverIdx === 2 ? "var(--blue-soft)" : "rgba(255,255,255,0.16)",
                  width: hoverIdx === 2 ? 40 : 18,
                  transition: "all 450ms cubic-bezier(.2,.7,.2,1)",
                  transform: "translateY(-2px)",
                }}
              />
              <span
                style={{
                  color: hoverIdx === 2 ? "rgba(255,255,255,0.70)" : "rgba(255,255,255,0.20)",
                  fontSize: "12px",
                  letterSpacing: "0.04em",
                  transition: "color 350ms ease",
                }}
              >
                Go to the library
              </span>
            </button>
          </div>

          {/* Contact—Brian Steck, Principal */}
          <div
            style={{
              marginTop: "clamp(28px,3.6vh,44px)",
              borderTop: "1px solid rgba(255,255,255,0.07)",
              paddingTop: "clamp(24px,3vh,36px)",
            }}
          >
            <div style={{
              border: "1px solid rgba(255,255,255,0.09)",
              background: "rgba(255,255,255,0.022)",
              padding: "clamp(20px,2.6vh,28px) clamp(20px,2.6vw,30px)",
              maxWidth: 520,
            }}>
              {/* Identity */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "clamp(12px,1.6vh,18px)", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ color: "#fff", fontSize: "15.5px", fontWeight: 500, letterSpacing: "-0.008em", lineHeight: 1.1 }}>
                    Brian Steck
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9.5px", letterSpacing: "0.10em", color: "rgba(255,255,255,0.30)", marginTop: 5, textTransform: "uppercase" }}>
                    Principal · Steck Aviation Management
                  </div>
                </div>
                <div style={{ width: 20, height: 1, background: "var(--blue)", opacity: 0.55, flexShrink: 0 }} />
              </div>

              {/* CTA */}
              <p style={{ color: "rgba(255,255,255,0.46)", fontSize: "13px", lineHeight: 1.72, margin: "0 0 clamp(14px,1.8vh,20px)", fontStyle: "normal" }}>
                For acquisition inquiries, ownership questions, or private walkthroughs—contact Brian Steck directly.
              </p>

              {/* Links */}
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <a
                  href="tel:9035399000"
                  style={{ color: "rgba(255,255,255,0.68)", fontSize: "13.5px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10, transition: "color 250ms" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.68)")}
                >
                  <Phone size={12} style={{ color: "#6791FF", flexShrink: 0 }} />
                  903-539-9000
                </a>
                <a
                  href="mailto:contact@steckaviationllc.com"
                  style={{ color: "rgba(255,255,255,0.68)", fontSize: "13.5px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10, transition: "color 250ms" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.68)")}
                >
                  <Mail size={12} style={{ color: "#6791FF", flexShrink: 0 }} />
                  contact@steckaviationllc.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
